/**
 * Shared bilaga-creation helpers (ADR-0028, 2027-05-27b addendum). The
 * Bilagor tab's "Lägg till bilaga" surface and the inline "Skapa bilaga"
 * dialog opened from an `attachment_ref` picker both create bilagor the
 * same way — these wrap the upload (presign → PUT → create) and the
 * create-from-template sequences so the two call sites can't drift.
 */

import { tenantApi, type ContractAttachment } from '../api.js';

export const BILAGA_MAX_BYTES = 25 * 1024 * 1024;
export const BILAGA_ACCEPTED_MIME = 'application/pdf';

/** Upload a PDF as a new bilaga (freeform unless `slotId` is given). */
export async function uploadBilaga(
  contractId: string,
  file: File,
  opts: { displayName?: string; slotId?: string | null } = {},
): Promise<ContractAttachment> {
  if (file.type !== BILAGA_ACCEPTED_MIME) {
    throw new Error('Endast PDF-filer stöds som bilaga just nu.');
  }
  if (file.size > BILAGA_MAX_BYTES) {
    throw new Error(
      `Filen är för stor (${(file.size / (1024 * 1024)).toFixed(1)} MB). Max 25 MB.`,
    );
  }
  const presign = await tenantApi.contract_attachments.upload_url({
    contract_id: contractId,
    filename: file.name,
    mime: file.type,
    bytes: file.size,
    slot_id: opts.slotId ?? undefined,
  });
  const putRes = await fetch(presign.upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': file.type },
  });
  if (!putRes.ok) {
    throw new Error(`Uppladdning misslyckades (${putRes.status})`);
  }
  const { attachment } = await tenantApi.contract_attachments.create({
    contract_id: contractId,
    attachment_id: presign.attachment_id,
    upload_key: presign.upload_key,
    filename: file.name,
    mime: file.type,
    bytes: file.size,
    display_name: opts.displayName,
    slot_id: opts.slotId ?? undefined,
  });
  return attachment;
}

/** Create a `kind='template'` bilaga from a registry-known (key, version). */
export async function createBilagaFromTemplate(
  contractId: string,
  templateKey: string,
  version: string,
  opts: { displayName?: string } = {},
): Promise<ContractAttachment> {
  // templates.get resolves the version_id for a registry-known (key,
  // version) without a dedicated attachment endpoint.
  const tg = await tenantApi.templates.get({
    template_key: templateKey,
    version,
  });
  const r = await tenantApi.contract_attachments.create_from_template({
    contract_id: contractId,
    template_version_id: tg.version_id,
    display_name: opts.displayName,
  });
  return r.attachment;
}
